'use client'

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Minus, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

/**
 * Canonical marketing FAQ accordion. This is the (previously triplicated)
 * implementation from CapabilitiesFAQ / HiveFAQ / vector FAQ, extracted once:
 * same framer-motion collapse, same Plus/Minus affordance, same show-more
 * logic, same button/region ARIA wiring. Page wrappers own their heading copy
 * and data; this owns the accordion.
 *
 * BlogFAQ intentionally stays separate — it uses a measured-height animation
 * and blog-layout positioning that this primitive does not replicate.
 */
export interface FAQAccordionItem {
  question: string
  answer: string
}

interface FAQAccordionProps {
  faqs: FAQAccordionItem[]
  /** Unique per page section so button/region ids never collide. */
  idPrefix: string
  initialVisibleCount?: number
  className?: string
}

export function FAQAccordion({
  faqs,
  idPrefix,
  initialVisibleCount = 4,
  className,
}: FAQAccordionProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(0)
  const [showAll, setShowAll] = useState(false)

  const visibleFAQs = showAll ? faqs : faqs.slice(0, initialVisibleCount)
  const hasMore = faqs.length > initialVisibleCount

  return (
    <div className={cn('space-y-4', className)}>
      <AnimatePresence initial={false}>
        {visibleFAQs.map((faq, index) => {
          const isOpen = activeIndex === index

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.3,
                delay:
                  index >= initialVisibleCount
                    ? (index - initialVisibleCount) * 0.1
                    : 0,
              }}
              className={cn(
                'border overflow-hidden transition-colors duration-200',
                isOpen
                  ? 'border-primary/20 bg-primary/5'
                  : 'border-border bg-card hover:border-primary/20'
              )}
            >
              <button
                id={`${idPrefix}-question-${index}`}
                onClick={() => setActiveIndex(isOpen ? null : index)}
                aria-expanded={isOpen}
                aria-controls={`${idPrefix}-answer-${index}`}
                className='flex items-center justify-between w-full p-6 text-left'
              >
                <h3 className='font-medium pr-8 text-base m-0'>
                  {faq.question}
                </h3>
                <span className='shrink-0 text-muted-foreground'>
                  {isOpen ? <Minus size={20} /> : <Plus size={20} />}
                </span>
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    id={`${idPrefix}-answer-${index}`}
                    role='region'
                    aria-labelledby={`${idPrefix}-question-${index}`}
                    initial='collapsed'
                    animate='open'
                    exit='collapsed'
                    variants={{
                      open: { opacity: 1, height: 'auto' },
                      collapsed: { opacity: 0, height: 0 },
                    }}
                    transition={{
                      duration: 0.3,
                      ease: [0.04, 0.62, 0.23, 0.98],
                    }}
                  >
                    <div className='px-6 pb-6 pt-0'>
                      <p className='text-muted-foreground'>{faq.answer}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )
        })}
      </AnimatePresence>

      {hasMore && (
        <div className='flex justify-center pt-4'>
          <Button
            variant='ghost'
            onClick={() => setShowAll(!showAll)}
            className='text-muted-foreground hover:text-foreground'
          >
            {showAll
              ? 'Show less'
              : `Show ${faqs.length - initialVisibleCount} more`}
            <ChevronDown
              className={cn(
                'w-4 h-4 ml-2 transition-transform duration-200',
                showAll && 'rotate-180'
              )}
            />
          </Button>
        </div>
      )}
    </div>
  )
}

export default FAQAccordion
